import routes from '../../routes';
import { bandittoApi } from '../../store/api/api';
import { ConsumerMessage, FileSettings, Message, SendMessageRequest } from './message.types';

interface CloseDialogResponse {
  id: string;
  consumerId: string;
  finalMessage: boolean;
}

export const MESSAGES_PAGE_SIZE = 10;

export type MessagesFilterType = 'UNREAD' | 'ME' | 'UNFINALIZED';

/**
 * A dialog is volatile: it changes its position, its unread counter and it leaves
 * the list once completed. So `size` is a growing window over page 0 instead of a
 * page number - every response replaces the whole list, always in the order and
 * with the filtering the server applied. Accumulating pages would keep rows that
 * the server no longer returns.
 */
type AllMessagesRequest = {
  size: number;
  type: MessagesFilterType;
  filterEmail: string;
};

const messageApi = bandittoApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllMessages: builder.query<Message[], AllMessagesRequest>({
      query: ({ size = MESSAGES_PAGE_SIZE, type = 'UNREAD', filterEmail = '' }) => {
        const params: Record<string, string | number> = { page: 0, size, type };
        if (filterEmail.trim()) {
          params.filterEmail = filterEmail.trim();
        }
        return {
          url: routes.api.allMessages(),
          params,
        };
      },
      // one cache entry per tab and search term, so a growing window keeps the
      // already loaded dialogs on screen while the next window is being fetched
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.type}-${queryArgs.filterEmail.trim()})`,
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.size !== previousArg?.size,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ consumerId }) => ({ type: 'Messages' as const, consumerId })),
              { type: 'Messages', id: 'LIST' },
              'Message',
            ]
          : [{ type: 'Messages', id: 'LIST' }, 'Message'],
    }),
    /** The number of dialogs waiting for an answer, counted by the server. */
    countUnfinalizedMessages: builder.query<{ count: number }, void>({
      query: () => routes.api.countUnfinalizedMessages(),
      providesTags: ['Message', 'Messages'],
    }),
    sendMessage: builder.mutation<ConsumerMessage, SendMessageRequest>({
      query: (message) => ({
        url: routes.api.allMessages(),
        method: 'POST',
        body: message,
      }),
      invalidatesTags: ['Message', 'Messages'],
    }),
    finalizeMessage: builder.mutation<CloseDialogResponse, string>({
      query: (id) => ({
        url: routes.api.finalizeMessage(),
        method: 'PATCH',
        body: { consumerId: id },
      }),
      invalidatesTags: ['Message', 'Messages'],
    }),
    unfinalizeMessage: builder.mutation<CloseDialogResponse, string>({
      query: (id) => ({
        url: routes.api.unfinalizeMessage(),
        method: 'PATCH',
        body: { consumerId: id },
      }),
      invalidatesTags: ['Message', 'Messages'],
    }),
    /**
     * Upload limits apimessages enforces on a chat attachment. They change only on a redeploy,
     * so the default cache is enough - the query is fired once when the chat opens.
     */
    getMessageFileSettings: builder.query<FileSettings, void>({
      query: () => routes.api.getMessageFileSettings(),
    }),
  }),
});

export const {
  useGetAllMessagesQuery,
  useCountUnfinalizedMessagesQuery,
  useSendMessageMutation,
  useFinalizeMessageMutation,
  useUnfinalizeMessageMutation,
  useGetMessageFileSettingsQuery,
} = messageApi;
